import { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { Camera, X, Image, Upload, Trash2, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { useTheme } from './ThemeContext';
import { useModal } from './AppModal';

export default function JobPhotos({ jobId, companyId, readOnly = false }) {
  const { colors } = useTheme();
  const { showSuccess, showError, showConfirm } = useModal();
  const [photos, setPhotos] = useState({ before: [], after: [], other: [] });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState('before');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadCategory, setUploadCategory] = useState('before');

  useEffect(() => {
    if (jobId) {
      loadPhotos();
    }
  }, [jobId]);

  const loadPhotos = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabaseData
        .from('job_photos')
        .select('*')
        .eq('job_id', jobId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Group by category
      const grouped = { before: [], after: [], other: [] };
      (data || []).forEach(photo => {
        if (grouped[photo.category]) {
          grouped[photo.category].push(photo);
        }
      });
      setPhotos(grouped);
    } catch (error) {

    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (category) => {
    setUploadCategory(category);
    fileInputRef.current?.click();
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check file types
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    const invalidFiles = files.filter(f => !validTypes.includes(f.type.toLowerCase()) && !f.type.startsWith('image/'));

    if (invalidFiles.length > 0) {
      showError('Please select image files only (JPEG, PNG, WebP, HEIC)');
      return;
    }

    // Check for video files (future feature)
    const videoFiles = files.filter(f => f.type.startsWith('video/'));
    if (videoFiles.length > 0) {
      showError('Video uploads coming soon! Please upload images for now.');
      return;
    }

    setUploading(true);
    try {
      for (const file of files) {
        // Create unique filename
        const fileExt = file.name.split('.').pop();
        const fileName = `${jobId}/${uploadCategory}/${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;

        // Upload to storage
        const { error: uploadError } = await supabase.storage
          .from('job-photos')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        // Get signed URL (private bucket)
        const { data: urlData } = await supabase.storage
          .from('job-photos')
          .createSignedUrl(fileName, 60 * 60 * 24 * 365); // 1 year expiry

        // Save to database
        const { error: dbError } = await supabaseData
          .from('job_photos')
          .insert([{
            company_id: companyId,
            job_id: jobId,
            category: uploadCategory,
            file_path: fileName,
            file_name: file.name,
            file_size: file.size,
            file_type: 'image',
            mime_type: file.type
          }]);

        if (dbError) throw dbError;
      }

      showSuccess(`${files.length} photo${files.length > 1 ? 's' : ''} uploaded!`);
      loadPhotos();
    } catch (error) {

      showError('Failed to upload: ' + error.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (photo) => {
    const confirmed = await showConfirm('Delete this photo?', {
      title: 'Delete Photo',
      confirmText: 'Delete',
      confirmStyle: 'danger'
    });
    if (!confirmed) return;

    try {
      // Delete from storage
      await supabase.storage
        .from('job-photos')
        .remove([photo.file_path]);

      // Delete from database
      const { error } = await supabaseData
        .from('job_photos')
        .delete()
        .eq('id', photo.id);

      if (error) throw error;

      showSuccess('Photo deleted');
      loadPhotos();
      setSelectedPhoto(null);
    } catch (error) {
      showError('Failed to delete: ' + error.message);
    }
  };

  const getPhotoUrl = async (photo) => {
    const { data } = await supabase.storage
      .from('job-photos')
      .createSignedUrl(photo.file_path, 60 * 60); // 1 hour
    return data?.signedUrl;
  };

  const PhotoThumbnail = ({ photo }) => {
    const [url, setUrl] = useState(null);

    useEffect(() => {
      getPhotoUrl(photo).then(setUrl);
    }, [photo.file_path]);

    return (
      <div
        onClick={() => setSelectedPhoto(photo)}
        style={{
          width: '80px',
          height: '80px',
          borderRadius: '8px',
          overflow: 'hidden',
          cursor: 'pointer',
          background: colors.bgInput,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `2px solid ${colors.border}`,
          flexShrink: 0
        }}
      >
        {url ? (
          <img
            src={url}
            alt={photo.file_name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <Image size={24} color={colors.textMuted} />
        )}
      </div>
    );
  };

  const CategorySection = ({ category, label, icon: Icon }) => {
    const categoryPhotos = photos[category] || [];
    const isExpanded = expandedCategory === category;

    return (
      <div style={{
        background: colors.bgCard,
        borderRadius: '10px',
        border: `1px solid ${colors.border}`,
        overflow: 'hidden',
        marginBottom: '0.75rem'
      }}>
        <div
          onClick={() => setExpandedCategory(isExpanded ? null : category)}
          style={{
            padding: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            background: isExpanded ? colors.bgInput : 'transparent'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Icon size={20} color={
              category === 'before' ? '#3b82f6' :
              category === 'after' ? '#22c55e' :
              '#f59e0b'
            } />
            <span style={{ fontWeight: '600', color: colors.text }}>{label}</span>
            <span style={{
              background: colors.bgInput,
              padding: '0.125rem 0.5rem',
              borderRadius: '10px',
              fontSize: '0.8rem',
              color: colors.textMuted
            }}>
              {categoryPhotos.length}
            </span>
          </div>
          {isExpanded ? <ChevronUp size={20} color={colors.textMuted} /> : <ChevronDown size={20} color={colors.textMuted} />}
        </div>

        {isExpanded && (
          <div style={{ padding: '1rem', borderTop: `1px solid ${colors.border}` }}>
            {categoryPhotos.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '1.5rem',
                color: colors.textMuted,
                fontSize: '0.9rem'
              }}>
                No {label.toLowerCase()} photos yet
              </div>
            ) : (
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                flexWrap: 'wrap',
                marginBottom: '1rem'
              }}>
                {categoryPhotos.map(photo => (
                  <PhotoThumbnail key={photo.id} photo={photo} />
                ))}
              </div>
            )}

            {!readOnly && (
              <button
                onClick={() => handleFileSelect(category)}
                disabled={uploading}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: uploading ? colors.border : colors.bgInput,
                  border: `2px dashed ${colors.border}`,
                  borderRadius: '8px',
                  color: uploading ? colors.textMuted : colors.text,
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}
              >
                {uploading && uploadCategory === category ? (
                  'Uploading...'
                ) : (
                  <>
                    <Camera size={18} />
                    Add {label} Photos
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Photo viewer modal
  const PhotoViewer = () => {
    const [url, setUrl] = useState(null);

    useEffect(() => {
      if (selectedPhoto) {
        getPhotoUrl(selectedPhoto).then(setUrl);
      }
    }, [selectedPhoto]);

    if (!selectedPhoto) return null;

    return (
      <div
        onClick={() => setSelectedPhoto(null)}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 3000,
          padding: '1rem'
        }}
      >
        <button
          onClick={() => setSelectedPhoto(null)}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '50%',
            padding: '0.75rem',
            cursor: 'pointer'
          }}
        >
          <X size={24} color="#fff" />
        </button>

        {!readOnly && (
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(selectedPhoto); }}
            style={{
              position: 'absolute',
              top: '1rem',
              left: '1rem',
              background: '#dc2626',
              border: 'none',
              borderRadius: '8px',
              padding: '0.5rem 1rem',
              cursor: 'pointer',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.9rem'
            }}
          >
            <Trash2 size={16} />
            Delete
          </button>
        )}

        <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90%', maxHeight: '85%' }}>
          {url ? (
            <img
              src={url}
              alt={selectedPhoto.file_name}
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                borderRadius: '8px',
                objectFit: 'contain'
              }}
            />
          ) : (
            <div style={{ color: '#fff' }}>Loading...</div>
          )}
          <div style={{
            textAlign: 'center',
            marginTop: '1rem',
            color: '#fff',
            fontSize: '0.9rem'
          }}>
            <span style={{
              background: selectedPhoto.category === 'before' ? '#3b82f6' :
                         selectedPhoto.category === 'after' ? '#22c55e' : '#f59e0b',
              padding: '0.25rem 0.75rem',
              borderRadius: '12px',
              textTransform: 'capitalize'
            }}>
              {selectedPhoto.category}
            </span>
            <div style={{ marginTop: '0.5rem', opacity: 0.7 }}>
              {new Date(selectedPhoto.created_at).toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted }}>
        Loading photos...
      </div>
    );
  }

  const totalPhotos = photos.before.length + photos.after.length + photos.other.length;

  return (
    <div>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleUpload}
        style={{ display: 'none' }}
      />

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Camera size={20} color={colors.accent} />
          <span style={{ fontWeight: '600', color: colors.text }}>Job Photos</span>
          {totalPhotos > 0 && (
            <span style={{
              background: colors.accent,
              color: '#000',
              padding: '0.125rem 0.5rem',
              borderRadius: '10px',
              fontSize: '0.8rem',
              fontWeight: 'bold'
            }}>
              {totalPhotos}
            </span>
          )}
        </div>
      </div>

      {/* Category sections */}
      <CategorySection category="before" label="Before" icon={Camera} />
      <CategorySection category="after" label="After" icon={Camera} />
      <CategorySection category="other" label="Other" icon={Image} />

      {/* Photo viewer modal */}
      <PhotoViewer />
    </div>
  );
}
